import React, { useState, useCallback, ReactNode, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';
import {Alert, AlertTitle, Button, Box, styled, Slide} from '@mui/material';
import { CircleCheck, XCircle, Info, AlertTriangle } from 'lucide-react';
/*import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';*/

// Tipi di Alert e Icone MUI 

type AlertType = 'success' | 'error' | 'warning' | 'info';

const ICON_MAP: Record<AlertType, ReactNode> = {
  success: <CircleCheck style={{ width:'3.5rem', height:'3.5rem' }} />,
  error: <XCircle style={{ width:'3.5rem', height:'3.5rem', marginBottom:'2rem' }} />,
  warning: <AlertTriangle style={{ width:'3.5rem', height:'3.5rem' }} />,
  info: <Info style={{ width:'3.5rem', height:'3.5rem' }} />,
};

//Stili del Contenitore Modale 

const ModalOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.4)', 
  zIndex: 9999, 
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(5),//2 
  pointerEvents: 'auto', 
}));

// Stili per il Toast 

const ToastContainer = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2), // Spazio dal bordo destro
  minWidth: '380px',
  maxWidth: '480px',
  height:'120px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper, 
}));


interface ConfirmToastModalProps {
  type: AlertType;
  title: string;
  alert: string;
  onConfirm: () => void;
  isOpen: boolean;
}

//Funzione principale
const ConfirmToastModal: React.FC<ConfirmToastModalProps> = ({
  type,
  title,
  alert,
  onConfirm,
  isOpen,
}) => {
  if (!isOpen) {
    return null;
  }

  //createPortal garantisce che sia sopra tutti gli altri elementi.
  return createPortal(
    <ModalOverlay onClick={(e) => e.stopPropagation()}> 
      <Slide direction="down" in={isOpen} mountOnEnter unmountOnExit>
        <ToastContainer>
          <Alert
            severity={type}
            icon={ICON_MAP[type]}
            sx={{
              paddingTop: '2rem',
              paddingBottom:'0.8rem',
              //maxHeight:'150px',
              //boxShadow:'0 0 4px 1px  #bbbbbbff',
              // Rimuove lo stile predefinito 
              '& .MuiAlert-action': { display: 'none' }, 
              display: 'flex',
              alignItems: 'center',
              // Stili per l'icona (che MUI gestisce tramite la classe .MuiAlert-icon)
              '& .MuiAlert-icon': {
                marginRight: '1rem', // Spazio a destra dell'icona
                paddingTop: '0', // Rimuove il padding predefinito
                opacity: 1, // Assicura che l'icona sia visibile (MUI a volte la nasconde)
                alignSelf: 'center', // Assicura che l'icona sia sempre a sinistra
              },
              // Stili per il contenitore del messaggio (title + alert + button)
              '& .MuiAlert-message': {
                flexGrow: 1, // Prende tutto lo spazio rimanente
                padding: 0, // Rimuove il padding predefinito
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center', // Centra verticalmente il contenuto del messaggio (se Alert non è troppo alto)
                alignItems: 'flex-start', // Modifica 3: Centra orizzontalmente il testo
                textAlign: 'left', // Modifica 4: Centra il testo
                minHeight: '80px', // Altezza minima per contenere il messaggio e il bottone
              }
            }}
          >
            {/* Messaggio */}
            <AlertTitle 
              sx={{ fontWeight: 'bold', marginBottom: '5px' }} 
              dangerouslySetInnerHTML={{ __html: title }} 
            />
            <Box 
              sx={{ width: '100%', textAlign: 'left' }} 
              dangerouslySetInnerHTML={{ __html: alert }} 
            />

            {/* Bottone di Conferma */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
              <Button
                variant = "outlined"
                color = {type}
                size = "small" 
                onClick = {onConfirm}
              >
                <strong>OK</strong>
              </Button>
            </Box>
          </Alert>
        </ToastContainer>
      </Slide>
    </ModalOverlay>,
    document.body
  );
};


// Tipo per le funzioni di alert 
interface AlertFunctions {
  warningAlert: (alert: string, title?: string) => Promise<{ isConfirmed: boolean }>;
  errorAlert: (alert: string, title?: string) => Promise<{ isConfirmed: boolean }>;
  infoAlert: (alert: string, title?: string) => Promise<{ isConfirmed: boolean }>;
  successAlert: (alert: string, title?: string) => Promise<{ isConfirmed: boolean }>;
}

//Context
const ConfirmToastContext = createContext<AlertFunctions>({
  warningAlert: () => Promise.resolve({ isConfirmed: true }),
  errorAlert: () => Promise.resolve({ isConfirmed: true }),
  infoAlert: () => Promise.resolve({ isConfirmed: true }),
  successAlert: () => Promise.resolve({ isConfirmed: true }),
});

//Hook 
export const useConfirmToast = () => useContext(ConfirmToastContext);

// Provider 
interface ConfirmToastProviderProps {
  children: ReactNode;
}

interface ToastData {
  type: AlertType;
  title: string;
  alert: string;
}

export const ConfirmToastProvider: React.FC<ConfirmToastProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const [resolver, setResolver] = useState<((value: { isConfirmed: boolean }) => void) | null>(null);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolver) {
      resolver({ isConfirmed: true });
      setResolver(null);
    }
    setToastData(null);
  }, [resolver]);

  const createButtonAlert = useCallback(
    (type: AlertType, alert: string, title: string): Promise<{ isConfirmed: boolean }> => {
      return new Promise((resolve) => {
        setToastData({ type, title, alert });
        setIsOpen(true);
        setResolver(() => resolve); 
      });
    },
    []
  );

  //Funzioni di utilità da esporre tramite il Context
  const alertFunctions: AlertFunctions = {
    warningAlert: (alert, title = 'Attenzione!') => createButtonAlert('warning', alert, title),
    errorAlert: (alert, title = 'Errore!') => createButtonAlert('error', alert, title),
    infoAlert: (alert, title = '') => createButtonAlert('info', alert, title),
    successAlert: (alert, title = '') => createButtonAlert('success', alert, title),
  };

  return (
    <ConfirmToastContext.Provider value={alertFunctions}>
      {children}
      {/*Modal viene renderizzato all'interno del Provider*/}
      {toastData && (
        <ConfirmToastModal
          type={toastData.type}
          title={toastData.title}
          alert={toastData.alert}
          onConfirm={handleConfirm}
          isOpen={isOpen}
        />
      )}
    </ConfirmToastContext.Provider>
  );
};